import React, { useEffect, useMemo, useState } from 'react'
import '../../App.css'
import Axios from 'axios'
import { useLocation } from 'react-router-dom'
import { IoTimerOutline, IoSearch } from 'react-icons/io5'
import { IoBan, IoCheckmark, IoCloseSharp } from 'react-icons/io5'
import ClipLoader from 'react-spinners/ClipLoader'
import DeclineRefundRequestModal from '../../modals/refunds/DeclineRefundRequestModal'
import ApproveRefundModal from '../../modals/refunds/ApproveRefundModal'
import { API_URL } from '../../config'

/* ---------- design tokens (shared with Analytics page) ---------- */
const C = {
  ink: '#14213d',
  muted: '#6b7590',
  line: '#e6e9f2',
  track: '#eef1f8',
  blue: '#2f6fed',
  up: '#12a06b',
  down: '#e5484d',
  amber: '#c77d0a',
}

const STATUS = {
  pending: { label: 'Pending', color: C.amber, bg: 'rgba(229,154,26,.14)', Icon: IoTimerOutline },
  approved: { label: 'Approved', color: C.up, bg: 'rgba(18,160,107,.12)', Icon: IoCheckmark },
  declined: { label: 'Declined', color: C.down, bg: 'rgba(229,72,77,.10)', Icon: IoBan },
  cancelled: { label: 'Cancelled', color: C.muted, bg: C.track, Icon: IoCloseSharp },
}

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap');

.rf { font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', sans-serif; color: ${C.ink}; padding: 8px 0 32px; }
.rf *, .rf *::before, .rf *::after { box-sizing: border-box; }
.rf h2, .rf p { margin: 0; }

.rf .rf-title { margin: 0; padding: 0; color: ${C.ink}; font-size: 22px; font-weight: 800; letter-spacing: -0.02em; line-height: 1.2; text-align: left; }
.rf .rf-sub { margin: 4px 0 0; padding: 0; color: ${C.muted}; font-size: 14px; font-weight: 500; text-align: left; }
.rf .rf-head { padding: 0; }

.rf-head { display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 12px; margin-bottom: 20px; }
.rf-title { font-size: 22px; font-weight: 800; letter-spacing: -0.02em; }
.rf-sub { margin-top: 4px; color: ${C.muted}; font-size: 14px; font-weight: 500; }

.rf-banner { display: flex; gap: 12px; padding: 14px 16px; margin-bottom: 20px; border-radius: 12px; background: rgba(47,111,237,.07); border: 1px solid rgba(47,111,237,.22); font-size: 13.5px; line-height: 1.55; }
.rf-banner b { display: block; margin-bottom: 2px; color: ${C.blue}; font-weight: 700; }

.rf-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; margin-bottom: 20px; }
.rf-stat { background: #fff; border: 1px solid ${C.line}; border-radius: 14px; padding: 16px 18px; }
.rf-stat small { display: block; font-size: 13px; color: ${C.muted}; font-weight: 600; }
.rf-stat b { display: block; margin-top: 8px; font-size: 24px; font-weight: 800; letter-spacing: -0.02em; font-variant-numeric: tabular-nums; }

.rf-card { background: #fff; border: 1px solid ${C.line}; border-radius: 14px; overflow: hidden; }
.rf-bar { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 16px; border-bottom: 1px solid ${C.line}; }
.rf-tabs { display: flex; flex-wrap: wrap; background: ${C.track}; border-radius: 9px; padding: 3px; gap: 2px; }
.rf-tab { border: 0; background: transparent; padding: 7px 13px; font: 700 12.5px/1 'Manrope', sans-serif; color: ${C.muted}; border-radius: 7px; cursor: pointer; white-space: nowrap; }
.rf-tab.on { background: #fff; color: ${C.ink}; box-shadow: 0 1px 2px rgba(20,33,61,.12); }
.rf-tab span { margin-left: 6px; font-weight: 600; opacity: .7; }
.rf-tab:focus-visible, .rf-btn:focus-visible, .rf-search input:focus-visible { outline: 2px solid ${C.blue}; outline-offset: 2px; }

.rf-search { position: relative; }
.rf-search svg { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: ${C.muted}; }
.rf-search input { width: 260px; max-width: 100%; height: 36px; padding: 0 12px 0 34px; border: 1px solid ${C.line}; border-radius: 9px; background: #fff; font: 500 13.5px 'Manrope', sans-serif; color: ${C.ink}; }

.rf-scroll { overflow-x: auto; }
.rf-table { width: 100%; min-width: 980px; border-collapse: collapse; font-variant-numeric: tabular-nums; }
.rf-table th { text-align: left; padding: 12px 16px; background: #f8f9fd; color: ${C.muted}; font-size: 12.5px; font-weight: 700; white-space: nowrap; border-bottom: 1px solid ${C.line}; }
.rf-table td { padding: 14px 16px; font-size: 13.5px; font-weight: 500; border-bottom: 1px solid ${C.line}; vertical-align: middle; }
.rf-table tbody tr:last-child td { border-bottom: 0; }
.rf-table tbody tr:hover { background: #fafbfe; }
.rf-r { text-align: right !important; }
.rf-muted { color: ${C.muted}; }
.rf-strong { font-weight: 700; }
.rf-ticket { font-weight: 700; letter-spacing: .01em; }
.rf-clip { max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.rf-pill { display: inline-flex; align-items: center; gap: 5px; padding: 4px 10px 4px 8px; border-radius: 999px; font-size: 12px; font-weight: 700; white-space: nowrap; }
.rf-src { margin-left: 6px; font-size: 12px; color: ${C.muted}; font-weight: 500; }

.rf-actions { display: flex; justify-content: flex-end; gap: 8px; }
.rf-btn { height: 34px; padding: 0 14px; border-radius: 8px; font: 700 13px 'Manrope', sans-serif; cursor: pointer; border: 1px solid transparent; }
.rf-btn.ok { background: ${C.up}; color: #fff; }
.rf-btn.ok:hover:not(:disabled) { background: #0e8a5c; }
.rf-btn.no { background: #fff; color: ${C.down}; border-color: rgba(229,72,77,.4); }
.rf-btn.no:hover:not(:disabled) { background: rgba(229,72,77,.06); }
.rf-btn:disabled { opacity: .45; cursor: not-allowed; }
.rf-busy { display: inline-flex; align-items: center; gap: 8px; justify-content: flex-end; width: 100%; color: ${C.muted}; font-size: 13px; font-weight: 600; }

.rf-empty { padding: 56px 16px; text-align: center; }
.rf-empty h3 { margin: 0 0 6px; font-size: 16px; font-weight: 700; }
.rf-empty p { color: ${C.muted}; font-size: 14px; }

.rf-toast { position: fixed; right: 20px; bottom: 20px; z-index: 2000; max-width: 360px; padding: 12px 16px; border-radius: 10px; color: #fff; font-size: 14px; font-weight: 600; box-shadow: 0 8px 24px rgba(20,33,61,.22); }
.rf-toast.ok { background: ${C.up}; }
.rf-toast.err { background: ${C.down}; }

@media (max-width: 900px) { .rf-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 460px) { .rf-stats { grid-template-columns: 1fr; } .rf-search input { width: 100%; } .rf-search { width: 100%; } }
`

const money = (v) => {
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''))
  const parts = (Number.isFinite(n) ? n : 0).toFixed(2).split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `R${parts.join('.')}`
}

const num = (v) => {
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

const StatusPill = ({ status, source }) => {
  const s = STATUS[status] || STATUS.pending
  const { Icon } = s
  return (
    <>
      <span className="rf-pill" style={{ color: s.color, background: s.bg }}>
        <Icon size={15} />
        {s.label}
      </span>
      {status === 'approved' && source ? <span className="rf-src">{source}</span> : null}
    </>
  )
}

const RefundRequests = () => {
  const location = useLocation()
  const { eventid } = location.state || {}

  const [requests, setRequests] = useState([])
  const [count, setCount] = useState(0)
  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')

  const [showModal, setShowModal] = useState(false)
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [refundId, setRefundId] = useState(null)
  const [refundAmount, setRefundAmount] = useState(null)
  const [processingId, setProcessingId] = useState(null)
  const [autoApprove, setAutoApprove] = useState(0)
  const [notice, setNotice] = useState(null)

  const baseURL = `${API_URL}/apps/kscanner/webservices.php`

  useEffect(() => {
    Axios.get(`${baseURL}?query=GET-REFUND-GOVERNANCE-RULES&eventid=${eventid}`, {
      withCredentials: true,
    })
      .then((response) => setAutoApprove(response.data.autoApprove))
      .catch((error) => setNotice({ type: 'err', message: String(error) }))

    Axios.get(`${baseURL}?query=GET-REFUND-REQUESTS&event_id=${eventid}`, {
      withCredentials: true,
    })
      .then((res) => {
        if (res.data.status === 'failed') setRequests([])
        else setRequests(res.data)
      })
      .catch((err) => console.log(err))
    // eslint-disable-next-line
  }, [count])

  // auto-dismiss notice
  useEffect(() => {
    if (!notice) return undefined
    const t = setTimeout(() => setNotice(null), 5000)
    return () => clearTimeout(t)
  }, [notice])

  const handleApproveClick = (id, ticketNumber, amount) => {
    setSelectedTicket(ticketNumber)
    setRefundId(id)
    setRefundAmount(amount)
    setShowApproveModal(true)
  }

  const handleDeclineClick = (id, ticketNumber) => {
    setSelectedTicket(ticketNumber)
    setRefundId(id)
    setShowModal(true)
  }

  const handleDecisionSubmit = async (id, approve, reason = '') => {
    const endpoint = approve ? 'approved' : 'declined'
    setProcessingId(refundId ?? id)
    setShowApproveModal(false)
    setShowModal(false)

    const data = new FormData()
    data.append('reason', reason)
    data.append('endpoint', endpoint)
    data.append('id', refundId ?? id)
    data.append('query', 'REFUND-REQUEST-RESPONSE')

    try {
      const res = await Axios({ method: 'POST', url: baseURL, data, withCredentials: true })
      if (res.data.status === 'success') {
        setNotice({ type: 'ok', message: res.data.message })
        setCount((c) => c + 1)
      } else {
        setNotice({ type: 'err', message: res.data.message })
      }
    } catch (err) {
      console.log(err)
      setNotice({ type: 'err', message: String(err) })
    }
    setProcessingId(null)
  }

  /* ---------- derived ---------- */
  const list = Array.isArray(requests) ? requests : []

  const stats = useMemo(() => {
    const s = { all: list.length, pending: 0, approved: 0, declined: 0, cancelled: 0, refunded: 0 }
    list.forEach((r) => {
      if (s[r.status] !== undefined) s[r.status] += 1
      if (r.status === 'approved') s.refunded += num(r.amount_refunded)
    })
    return s
  }, [list])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return list.filter((r) => {
      if (filter !== 'all' && r.status !== filter) return false
      if (!q) return true
      return [r.buyer_name, r.ticket_number, r.refund_reason, r.comments]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    })
  }, [list, filter, query])

  const tabs = [
    ['all', 'All'],
    ['pending', 'Pending'],
    ['approved', 'Approved'],
    ['declined', 'Declined'],
    ['cancelled', 'Cancelled'],
  ]

  const locked = Number(autoApprove) === 1

  return (
    <div className="rf">
      <style>{styles}</style>

      <div className="rf-head">
        <div>
          <h2 className="rf-title">Refund requests</h2>
          <p className="rf-sub">Review and respond to refund requests from ticket buyers.</p>
        </div>
      </div>

      {locked && (
        <div className="rf-banner" role="status">
          <div>
            <b>Refunds are being processed automatically by Karamale</b>
            Each request is evaluated without organizer input, using your refund rules if set, or
            Karamale's default policies if not. Manual approve and decline are turned off while
            automation is active. You can switch to manual review in your event configuration
            settings.
          </div>
        </div>
      )}

      <div className="rf-stats">
        <div className="rf-stat">
          <small>Total requests</small>
          <b>{stats.all}</b>
        </div>
        <div className="rf-stat">
          <small>Awaiting review</small>
          <b style={{ color: stats.pending ? C.amber : C.ink }}>{stats.pending}</b>
        </div>
        <div className="rf-stat">
          <small>Approved</small>
          <b>{stats.approved}</b>
        </div>
        <div className="rf-stat">
          <small>Total refunded</small>
          <b>{money(stats.refunded)}</b>
        </div>
      </div>

      <div className="rf-card">
        <div className="rf-bar">
          <div className="rf-tabs" role="tablist" aria-label="Filter by status">
            {tabs.map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={filter === key}
                className={`rf-tab ${filter === key ? 'on' : ''}`}
                onClick={() => setFilter(key)}
              >
                {label}
                <span>{stats[key]}</span>
              </button>
            ))}
          </div>
          <label className="rf-search">
            <IoSearch size={16} />
            <input
              type="search"
              placeholder="Search buyer, ticket or reason"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search refund requests"
            />
          </label>
        </div>

        <div className="rf-scroll">
          <table className="rf-table">
            <thead>
              <tr>
                <th>Requested</th>
                <th>Buyer</th>
                <th>Ticket</th>
                <th className="rf-r">Original</th>
                <th className="rf-r">Refund</th>
                <th>Reason</th>
                <th>Comments</th>
                <th>Status</th>
                <th className="rf-r">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((item) => {
                const busy = processingId === item.refund_id
                const pending = item.status === 'pending'
                return (
                  <tr key={item.id}>
                    <td className="rf-muted">{item.requested_at}</td>
                    <td className="rf-strong">{item.buyer_name}</td>
                    <td className="rf-ticket">{item.ticket_number}</td>
                    <td className="rf-r">{money(item.original_amount)}</td>
                    <td className="rf-r rf-strong">{money(item.amount_refunded)}</td>
                    <td>{item.refund_reason}</td>
                    <td className="rf-muted">
                      <div className="rf-clip" title={item.comments}>
                        {item.comments || '-'}
                      </div>
                    </td>
                    <td>
                      <StatusPill status={item.status} source={item.source} />
                    </td>
                    <td className="rf-r">
                      {!pending ? (
                        <span className="rf-muted">No action needed</span>
                      ) : busy ? (
                        <span className="rf-busy">
                          <ClipLoader color={C.muted} loading={true} size={16} />
                          Processing
                        </span>
                      ) : (
                        <div className="rf-actions">
                          <button
                            type="button"
                            className="rf-btn ok"
                            disabled={locked}
                            onClick={() =>
                              handleApproveClick(
                                item.refund_id,
                                item.ticket_number,
                                item.amount_refunded,
                              )
                            }
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            className="rf-btn no"
                            disabled={locked}
                            onClick={() => handleDeclineClick(item.refund_id, item.ticket_number)}
                          >
                            Decline
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          {visible.length === 0 && (
            <div className="rf-empty">
              {list.length === 0 ? (
                <>
                  <h3>No refund requests yet</h3>
                  <p>When a buyer requests a refund, it will show up here for review.</p>
                </>
              ) : (
                <>
                  <h3>No matching requests</h3>
                  <p>Try a different status filter or search term.</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {notice && (
        <div className={`rf-toast ${notice.type}`} role="status">
          {notice.message}
        </div>
      )}

      <DeclineRefundRequestModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={(reason) => handleDecisionSubmit(refundId, false, reason)}
        ticketNumber={selectedTicket}
        refundId={refundId}
      />

      <ApproveRefundModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={() => handleDecisionSubmit(refundId, true)}
        ticketNumber={selectedTicket}
        refundAmount={refundAmount}
      />
    </div>
  )
}

export default RefundRequests
